import React from 'react';
import * as ETVConstants from '../ETVConstants';
import {
  Alert, Box, Button, Card, Checkbox, Chip, FormControlLabel, IconButton, TextField, Typography
} from '@mui/material';
import { Add, Delete, Edit, ImageOutlined, PlaceOutlined } from '@mui/icons-material';
import ETVPopup from '../tools/ETVPopup';
import ETVActionButton from '../tools/ETVActionButton';

const WEEKDAYS = [
  'monday', 'tuesday', 'wednesday', 'thursday',
  'friday', 'saturday', 'sunday'
];
const TIME_OPTIONS = Array.from({ length: 24 * 4 }, (_, index) => {
  const hours = String(Math.floor(index / 4)).padStart(2, '0');
  const minutes = String((index % 4) * 15).padStart(2, '0');
  return `${hours}:${minutes}`;
});
const PLACE_IMAGE_SIZE = 512;
const PLACE_IMAGE_QUALITY = 0.85;

class FloorWayfindingEditor extends React.Component {
  state = {
    dialogOpen: false,
    editIndex: -1,

    label: '',
    unitNumber: '',
    kind: 'shop',
    x: '',
    y: '',
    keywordInput: '',
    keywords: [],
    openingDays: [...WEEKDAYS],
    openingFrom: '',
    openingUntil: '',
    imageFile: null,
    imageUrl: '',
    imagePath: '',
    imagePreviewUrl: '',
    imageObjectUrl: '',
    imageError: '',
    isUploadingImage: false,
  };

  componentWillUnmount() {
    this.releaseImageObjectUrl();
  }

  releaseImageObjectUrl = () => {
    if (this.state.imageObjectUrl) URL.revokeObjectURL(this.state.imageObjectUrl);
  };

  openAddDialog = () => {
    this.releaseImageObjectUrl();
    this.setState({
      dialogOpen: true,
      editIndex: -1,
      label: '',
      unitNumber: '',
      kind: 'shop',
      x: '',
      y: '',
      keywordInput: '',
      keywords: [],
      openingDays: [...WEEKDAYS],
      openingFrom: '',
      openingUntil: '',
      imageFile: null,
      imageUrl: '',
      imagePath: '',
      imagePreviewUrl: '',
      imageObjectUrl: '',
      imageError: '',
      isUploadingImage: false,
    });
  };

  openEditDialog = (node, index) => {
    this.releaseImageObjectUrl();
    this.setState({
      dialogOpen: true,
      editIndex: index,
      label: node.label || '',
      unitNumber: node.unit_number || '',
      kind: node.kind || 'shop',
      x: node.x ?? '',
      y: node.y ?? '',
      keywordInput: '',
      keywords: Array.isArray(node.keywords) ? node.keywords : [],
      openingDays: Array.isArray(node.opening_hours?.days)
        ? node.opening_hours.days
        : [...WEEKDAYS],
      openingFrom: TIME_OPTIONS.includes(node.opening_hours?.from)
        ? node.opening_hours.from
        : '',
      openingUntil: TIME_OPTIONS.includes(node.opening_hours?.until)
        ? node.opening_hours.until
        : '',
      imageFile: null,
      imageUrl: node.image_url || '',
      imagePath: node.image_path || '',
      imagePreviewUrl: node.image_url || '',
      imageObjectUrl: '',
      imageError: '',
      isUploadingImage: false,
    });
  };

  closeDialog = () => {
    if (this.state.isUploadingImage) return;
    this.releaseImageObjectUrl();
    this.setState({ dialogOpen: false });
  };

  getNodeStyle = (kind) => {
    switch(kind) {
      case 'shop':
        return {
          bgcolor: '#E3F2FD',
          color: '#1565C0'
        };

      case 'restaurant':
        return {
          bgcolor: '#FFF8E1',
          color: '#F57F17'
        };

      case 'coffee_shop':
        return {
          bgcolor: '#EFEBE9',
          color: '#6D4C41'
        };

      case 'office':
        return {
          bgcolor: '#E8F5E9',
          color: '#2E7D32'
        };

      case 'facility':
        return {
          bgcolor: '#FFF3E0',
          color: '#EF6C00'
        };

      case 'general':
        return {
          bgcolor: '#ECEFF1',
          color: '#455A64'
        };

      case 'info_point':
        return {
          bgcolor: '#E0F7FA',
          color: '#00796B'
        };

      case 'emergency_kit':
        return {
          bgcolor: '#FFEBEE',
          color: '#C62828'
        };

      case 'lift':
        return {
          bgcolor: '#F3E5F5',
          color: '#7B1FA2'
        };

      case 'stairs':
        return {
          bgcolor: '#ECEFF1',
          color: '#455A64'
        };

      case 'escalator':
        return {
          bgcolor: '#FCE4EC',
          color: '#C2185B'
        };

      default:
        return {
          bgcolor: '#EEEEEE',
          color: '#616161'
        };
    }
  };  

  addKeyword = () => {
    const keyword = (this.state.keywordInput || '').trim();
    if (!keyword) return;

    if (this.state.keywords.includes(keyword)) {
      this.setState({ keywordInput: '' });
      return;
    }

    this.setState(s => ({
      keywords: [...s.keywords, keyword],
      keywordInput: '',
    }));
  };

  removeKeyword = (idx) => {
    this.setState(s => ({
      keywords: s.keywords.filter((_, i) => i !== idx),
    }));
  };

  handleKeywordKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.addKeyword();
    }
  };

  toggleOpeningDay = (day) => {
    this.setState(s => ({
      openingDays: s.openingDays.includes(day)
        ? s.openingDays.filter(value => value !== day)
        : WEEKDAYS.filter(value => [...s.openingDays, day].includes(value)),
    }));
  };

  standardizeImage = (file) => new Promise((resolve, reject) => {
    const sourceUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = PLACE_IMAGE_SIZE;
        canvas.height = PLACE_IMAGE_SIZE;
        const context = canvas.getContext('2d');
        if (!context) throw new Error('canvas_unavailable');

        const cropSize = Math.min(image.naturalWidth, image.naturalHeight);
        const sourceX = (image.naturalWidth - cropSize) / 2;
        const sourceY = (image.naturalHeight - cropSize) / 2;
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, PLACE_IMAGE_SIZE, PLACE_IMAGE_SIZE);
        context.drawImage(
          image,
          sourceX, sourceY, cropSize, cropSize,
          0, 0, PLACE_IMAGE_SIZE, PLACE_IMAGE_SIZE
        );
        canvas.toBlob(blob => {
          URL.revokeObjectURL(sourceUrl);
          if (!blob) {
            reject(new Error('image_conversion_failed'));
            return;
          }
          const baseName = file.name.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9_-]/g, '_') || 'place';
          resolve(new File([blob], `${baseName}_512x512.jpg`, { type: 'image/jpeg' }));
        }, 'image/jpeg', PLACE_IMAGE_QUALITY);
      } catch (error) {
        URL.revokeObjectURL(sourceUrl);
        reject(error);
      }
    };
    image.onerror = () => {
      URL.revokeObjectURL(sourceUrl);
      reject(new Error('invalid_image'));
    };
    image.src = sourceUrl;
  });

  handleImageChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type) || file.size > 5 * 1024 * 1024) {
      this.setState({ imageError: ETVConstants.trans('place_image_file_error') });
      return;
    }

    try {
      const standardizedFile = await this.standardizeImage(file);
      const objectUrl = URL.createObjectURL(standardizedFile);
      this.releaseImageObjectUrl();
      this.setState({
        imageFile: standardizedFile,
        imagePreviewUrl: objectUrl,
        imageObjectUrl: objectUrl,
        imageError: '',
      });
    } catch {
      this.setState({ imageError: ETVConstants.trans('place_image_file_error') });
    }
  };

  removeImage = () => {
    this.releaseImageObjectUrl();
    this.setState({
      imageFile: null,
      imageUrl: '',
      imagePath: '',
      imagePreviewUrl: '',
      imageObjectUrl: '',
      imageError: '',
    });
  };

  uploadImage = async () => {
    if (!this.state.imageFile) {
      return { url: this.state.imageUrl || '', path: this.state.imagePath || '' };
    }
    const formData = new FormData();
    formData.append('file', this.state.imageFile);
    const response = await fetch(`${ETVConstants.getServerBase()}/place_image_upload`, {
      method: 'POST',
      headers: {
        user_id: localStorage.getItem('etv.user_id'),
        access_token: localStorage.getItem('etv.access_token'),
      },
      body: formData,
    });
    const result = await response.json();
    if (!response.ok || !result.image_url || !result.value) {
      throw new Error(result.message || result.error || 'image_upload_failed');
    }
    if (this.props.onImageUploaded) this.props.onImageUploaded(result.value);
    return { url: result.image_url, path: result.value };
  };

  saveNode = async () => {
    const label = (this.state.label || '').trim();
    if (!label) return;

    this.setState({ isUploadingImage: true, imageError: '' });
    let imageAsset;
    try {
      imageAsset = await this.uploadImage();
    } catch {
      this.setState({
        isUploadingImage: false,
        imageError: ETVConstants.trans('place_image_upload_error'),
      });
      return;
    }

    const existingNode = this.state.editIndex >= 0
      ? this.props.nodes[this.state.editIndex]
      : {};
    const node = {
      ...existingNode,
      id: this.state.editIndex >= 0
        ? existingNode.id
        : `node_${Date.now()}`,
      label,
      unit_number: (this.state.unitNumber || '').trim(),
      kind: this.state.kind || 'shop',
      x: Number(this.state.x) || 0,
      y: Number(this.state.y) || 0,
      keywords: this.state.keywords || [],
      opening_hours: {
        days: this.state.openingDays || [],
        from: this.state.openingFrom || '',
        until: this.state.openingUntil || '',
      },
      image_url: imageAsset.url,
      image_path: imageAsset.path,
    };

    const nodes = [...(this.props.nodes || [])];

    if (this.state.editIndex >= 0) {
      nodes[this.state.editIndex] = node;
    } else {
      nodes.push(node);
    }

    this.props.onChange(nodes);
    this.releaseImageObjectUrl();
    this.setState({ isUploadingImage: false, dialogOpen: false, imageObjectUrl: '' });
  };

  removeNode = (idx) => {
    this.props.onChange((this.props.nodes || []).filter((_, i) => i !== idx));
  };

  getOpeningHoursLabel = (node) => {
    const hours = node.opening_hours;
    if (!hours?.from || !hours?.until) return '';
    const days = Array.isArray(hours.days) ? hours.days : [];
    let dayLabel = '';
    if (days.length === 7) {
      dayLabel = `${ETVConstants.trans('monday')}–${ETVConstants.trans('sunday')}`;
    } else if (days.length > 0) {
      dayLabel = days.map(day => ETVConstants.trans(day).slice(0, 3)).join(', ');
    }
    return `${dayLabel}${dayLabel ? ' · ' : ''}${hours.from}–${hours.until}`;
  };

  render() {
    const nodes = this.props.nodes || [];

    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        {this.props.showAddButton !== false && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={this.openAddDialog}
            >
              {ETVConstants.trans("add_node")}
            </Button>
          </Box>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 1,
            alignItems: 'start',
          }}
        >
          {nodes.map((node, idx) => (
            <Card
              key={node.id || idx}
              variant="outlined"
              sx={{
                overflow: 'hidden',
                borderRadius: 2,
                transition: 'box-shadow 160ms ease, transform 160ms ease',
                '&:hover': { boxShadow: 3, transform: 'translateY(-1px)' },
              }}
            >
              <Box sx={{ display: 'flex', minHeight: 116 }}>
                <Box
                  sx={{
                    width: 112,
                    minWidth: 112,
                    bgcolor: '#f1f5f9',
                    display: 'grid',
                    placeItems: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <PlaceOutlined sx={{ fontSize: 38, color: '#94a3b8' }} />
                  {node.image_url && (
                    <Box
                      component="img"
                      src={node.image_url}
                      alt={node.label || ''}
                      onError={(event) => { event.currentTarget.style.display = 'none'; }}
                      sx={{ width: '100%', height: '100%', position: 'absolute', objectFit: 'cover' }}
                    />
                  )}
                </Box>

                <Box sx={{ p: 1.25, minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.5 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" fontWeight={700} noWrap title={node.label}>
                        {node.label}
                      </Typography>
                      {node.unit_number && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {ETVConstants.trans('unit_number')}: {node.unit_number}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignSelf: 'flex-start', ml: 'auto' }}>
                      <IconButton size="small" aria-label={ETVConstants.trans('edit')} onClick={() => this.openEditDialog(node, idx)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" aria-label={ETVConstants.trans('delete')} onClick={() => this.removeNode(idx)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  <Chip
                    size="small"
                    label={ETVConstants.trans(node.kind)}
                    sx={{
                      mt: 0.75,
                      alignSelf: 'flex-start',
                      height: 22,
                      bgcolor: this.getNodeStyle(node.kind).bgcolor,
                      color: this.getNodeStyle(node.kind).color,
                      fontWeight: 600
                    }}
                  />

                  {this.getOpeningHoursLabel(node) && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75 }}>
                      {this.getOpeningHoursLabel(node)}
                    </Typography>
                  )}

                  {(node.keywords || []).length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 'auto', pt: 0.75 }}>
                      {(node.keywords || []).slice(0, 3).map((kw, kidx) => (
                        <Chip key={`${kw}-${kidx}`} size="small" label={kw} variant="outlined" sx={{ height: 20 }} />
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            </Card>
          ))}

          {nodes.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ gridColumn: '1 / -1' }}>
              No shops/offices added yet.
            </Typography>
          )}
        </Box>

        <ETVPopup
          open={this.state.dialogOpen}
          onClose={this.closeDialog}
          width="small"
          icon={this.state.editIndex >= 0 ? <Edit /> : <PlaceOutlined />}
          title={this.state.editIndex >= 0
            ? (ETVConstants.trans('edit_place') || 'Edit place')
            : (ETVConstants.trans('add_node') || 'Add place')}
          description={ETVConstants.trans('place_description') || 'Define a shop, office, facility, or access point for wayfinding.'}
          bottomActions={
            <>
              <ETVActionButton action="cancel" disabled={this.state.isUploadingImage} onClick={this.closeDialog} />
              {this.state.editIndex >= 0 ? (
                <ETVActionButton action="update" loading={this.state.isUploadingImage} onClick={this.saveNode} />
              ) : (
                <ETVActionButton action="save" loading={this.state.isUploadingImage} label={ETVConstants.trans('add_node') || 'Add place'} onClick={this.saveNode} />
              )}
            </>
          }
        >
            <Box sx={{ display: 'grid', gap: 2 }}>
              <TextField
                label={ETVConstants.trans('place_name')}
                fullWidth
                value={this.state.label}
                onChange={(e) => this.setState({ label: e.target.value })}
                placeholder="e.g. Starbucks"
              />

              <TextField
                label={ETVConstants.trans('unit_number')}
                fullWidth
                value={this.state.unitNumber}
                onChange={(e) => this.setState({ unitNumber: e.target.value })}
                placeholder="e.g. #01-12"
              />

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {ETVConstants.trans('place_image')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  {this.state.imagePreviewUrl && (
                    <Box
                      component="img"
                      src={this.state.imagePreviewUrl}
                      alt=""
                      sx={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                    />
                  )}
                  <Box sx={{ display: 'grid', gap: 0.5 }}>
                    <Button variant="outlined" component="label" startIcon={<ImageOutlined />} disabled={this.state.isUploadingImage}>
                      {ETVConstants.trans('upload_square_image')}
                      <input hidden type="file" accept="image/jpeg,image/png" onChange={this.handleImageChange} />
                    </Button>
                    {this.state.imagePreviewUrl && (
                      <Button size="small" color="error" onClick={this.removeImage} disabled={this.state.isUploadingImage}>
                        {ETVConstants.trans('remove_image')}
                      </Button>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {ETVConstants.trans('place_image_help')}
                    </Typography>
                  </Box>
                </Box>
                {this.state.imageError && <Alert severity="error" sx={{ mt: 1 }}>{this.state.imageError}</Alert>}
              </Box>

              <TextField
                select
                SelectProps={{ native: true }}
                label="Type"
                fullWidth
                value={this.state.kind}
                onChange={(e) => this.setState({ kind: e.target.value })}
              >
                <option value="shop">{ETVConstants.trans("shop")}</option>
                <option value="restaurant">{ETVConstants.trans("restaurant")}</option>
                <option value="coffee_shop">{ETVConstants.trans("coffee_shop")}</option>
                <option value="office">{ETVConstants.trans("office")}</option>
                <option value="facility">{ETVConstants.trans("facility")}</option>
                <option value="general">{ETVConstants.trans("general")}</option>
                <option value="info_point">{ETVConstants.trans("info_point")}</option>
                <option value="emergency_kit">{ETVConstants.trans("emergency_kit")}</option>
                <option value="lift">{ETVConstants.trans("lift")}</option>
                <option value="stairs">{ETVConstants.trans("stairs")}</option>
                <option value="escalator">{ETVConstants.trans("escalator")}</option>
              </TextField>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <TextField
                  label={ETVConstants.trans("x_coordinate")}
                  type="number"
                  value={this.state.x}
                  onChange={(e) => this.setState({ x: e.target.value })}
                />

                <TextField
                  label={ETVConstants.trans("y_coordinate")}
                  type="number"
                  value={this.state.y}
                  onChange={(e) => this.setState({ y: e.target.value })}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  {ETVConstants.trans('opening_times')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
                  {WEEKDAYS.map(day => (
                    <FormControlLabel
                      key={day}
                      sx={{ mr: 1 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={this.state.openingDays.includes(day)}
                          onChange={() => this.toggleOpeningDay(day)}
                        />
                      }
                      label={ETVConstants.trans(day)}
                    />
                  ))}
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
                  <TextField
                    label={ETVConstants.trans('from_time')}
                    select
                    SelectProps={{ native: true }}
                    value={this.state.openingFrom}
                    onChange={(e) => this.setState({ openingFrom: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  >
                    <option value="" />
                    {TIME_OPTIONS.map(time => <option key={time} value={time}>{time}</option>)}
                  </TextField>
                  <TextField
                    label={ETVConstants.trans('until_time')}
                    select
                    SelectProps={{ native: true }}
                    value={this.state.openingUntil}
                    onChange={(e) => this.setState({ openingUntil: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  >
                    <option value="" />
                    {TIME_OPTIONS.map(time => <option key={time} value={time}>{time}</option>)}
                  </TextField>
                </Box>
              </Box>

              <TextField
                label={ETVConstants.trans("add_keyword")}
                fullWidth
                value={this.state.keywordInput}
                onChange={(e) => this.setState({ keywordInput: e.target.value })}
                onKeyDown={this.handleKeywordKeyDown}
                placeholder={"Type keyword and press Enter"}
              />

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {this.state.keywords.map((kw, idx) => (
                  <Chip
                    key={`${kw}-${idx}`}
                    label={kw}
                    onDelete={() => this.removeKeyword(idx)}
                    variant="outlined"
                  />
                ))}

                {this.state.keywords.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {ETVConstants.trans("no_keywords_yet")}
                  </Typography>
                )}
              </Box>
            </Box>
        </ETVPopup>
      </Box>
    );
  }
}

export default FloorWayfindingEditor;
